import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const MEDICINE_ICONS = [
    { icon: '💊', label: 'Cápsula', bg: 'bg-[#ff5874]', border: 'border-[#ff5874]' },
    { icon: '🧪', label: 'Jarabe', bg: 'bg-[#008b8b]', border: 'border-[#008b8b]' },
    { icon: '💉', label: 'Inyección', bg: 'bg-[#00595b]', border: 'border-[#00595b]' },
    { icon: '🧫', label: 'Comprimido', bg: 'bg-[#e63946]', border: 'border-[#e63946]' }
];

const StaffPollProjection = () => {
    const { config } = useConfig();
    const [poll, setPoll] = useState(null);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const primaryColor = config?.primaryColor || '#00595b';
    const secondaryColor = config?.secondaryColor || '#ff5874';

    useEffect(() => {
        fetchActivePollAndStats();
        const interval = setInterval(fetchActivePollAndStats, 2000);
        return () => clearInterval(interval);
    }, []);

    const fetchActivePollAndStats = async () => {
        try {
            const activeRes = await api.get('/polls/active');
            if (activeRes.data) {
                setPoll(activeRes.data);
                const statsRes = await api.get(`/polls/${activeRes.data.id}/stats`);
                setStats(statsRes.data);
            } else {
                setPoll(null);
                setStats(null);
            }
        } catch (error) {
            console.error("Error fetching active poll projection", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="w-16 h-16 border-4 border-white/20 border-t-[#ff5874] rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!poll) {
        return (
            <div className="min-h-[85vh] flex flex-col items-center justify-center p-8 text-center animate-fadeIn">
                <div 
                    style={{ backgroundColor: primaryColor, borderColor: secondaryColor }}
                    className="max-w-2xl w-full p-12 rounded-[40px] border-4 shadow-2xl space-y-6 text-white"
                >
                    <div className="text-7xl animate-pulse">📺</div>
                    <h1 className="text-4xl font-black uppercase tracking-tight">
                        Pantalla de Proyección de Trivia
                    </h1>
                    <p className="text-lg font-bold uppercase tracking-widest opacity-90">
                        Esperando activación de pregunta interactiva...
                    </p>
                    <div className="pt-4 flex justify-center gap-3">
                        <span className="px-6 py-2 rounded-full bg-white/20 text-xs font-black uppercase tracking-widest animate-bounce">
                            ● Transmisión En Vivo
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[85vh] flex flex-col justify-between p-6 sm:p-10 animate-fadeIn max-w-6xl mx-auto font-sans">
            {/* Header Question Box for Stage / TV */}
            <div className="relative mb-8">
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-20">
                    <span 
                        style={{ backgroundColor: secondaryColor }} 
                        className="px-8 py-2.5 rounded-full text-white text-sm font-black uppercase tracking-[0.2em] shadow-xl inline-flex items-center gap-3"
                    >
                        <span className="w-3 h-3 rounded-full bg-white animate-ping"></span>
                        Trivia Interactiva En Escenario
                    </span>
                </div>

                <div 
                    style={{ borderColor: secondaryColor }}
                    className="bg-white rounded-[35px] border-4 pt-12 pb-8 px-8 sm:px-14 shadow-2xl text-center relative overflow-hidden"
                >
                    <h1 style={{ color: primaryColor }} className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight uppercase tracking-tight">
                        {poll.question}
                    </h1>
                    <div className="mt-4 flex items-center justify-center gap-6 text-slate-400 text-xs sm:text-sm font-black uppercase tracking-widest">
                        <span>Total Votos: <strong className="text-slate-800 text-lg">{stats?.totalVotes || 0}</strong></span>
                        <span>•</span>
                        <span>Participación: <strong className="text-slate-800 text-lg">{stats?.totalUsers > 0 ? Math.round(((stats?.totalVotes || 0) / stats.totalUsers) * 100) : 0}%</strong></span>
                    </div>
                </div>
            </div>

            {/* Projection Grid Options Kahoot Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-auto">
                {poll.options.map((opt, idx) => {
                    const medicine = MEDICINE_ICONS[idx % MEDICINE_ICONS.length];
                    const voteObj = stats?.results?.find(r => r.id === opt.id);
                    const votesCount = voteObj ? voteObj.votes : 0;
                    const percent = stats?.totalVotes > 0 ? Math.round((votesCount / stats.totalVotes) * 100) : 0;

                    return (
                        <div
                            key={opt.id}
                            className="bg-white border-2 border-slate-200 rounded-3xl p-6 shadow-xl flex flex-col justify-between relative overflow-hidden group hover:scale-[1.01] transition-all duration-300"
                        >
                            <div className="flex items-center gap-5 z-10">
                                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shrink-0 ${medicine.bg} text-white shadow-lg`}>
                                    {medicine.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <span className="text-xs font-black uppercase text-slate-400 block tracking-widest mb-1">
                                        Opción {String.fromCharCode(65 + idx)}
                                    </span>
                                    <h3 className="font-black text-xl sm:text-2xl text-slate-800 leading-tight">
                                        {opt.option_text}
                                    </h3>
                                </div>
                                <div className="text-right shrink-0">
                                    <span style={{ color: primaryColor }} className="text-3xl sm:text-4xl font-black block">
                                        {percent}%
                                    </span>
                                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">
                                        {votesCount} votos
                                    </span>
                                </div>
                            </div>

                            {/* Progress bar background indicator */}
                            <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden border border-slate-200 mt-6 p-0.5 shadow-inner">
                                <div
                                    className={`h-full rounded-full transition-all duration-1000 shadow-md ${medicine.bg}`}
                                    style={{ width: `${percent}%` }}
                                ></div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Projection Footer Disclaimer */}
            <div className="text-center pt-6">
                <p className="text-xs font-black uppercase tracking-[0.25em] text-white/90">
                    Responde desde tu dispositivo móvil en vivo seleccionando tu opción
                </p>
            </div>
        </div>
    );
};

export default StaffPollProjection;
